import {
  Badge,
  BulkActionsBar,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Skeleton,
  useToast,
} from '@citizen-walk/shared-ui';
import {
  getModeTemplate,
  listPostAnalysisOptions,
  PHOTO_PRIVACY_CATEGORY_LABELS,
  photoPrivacyCategory,
  resolvePhotoPrivacySettings,
  type PhotoPrivacyCategory,
  type DataSettings,
  type DistrictMode,
  type FieldDef,
  type PostVisibility,
} from '@citizen-walk/shared-types';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { storage } from '../../lib/firebase';
import { shortUid } from '../../lib/uid';
import { useAdminAuth } from '../auth';
import { useDistrict } from '../districts-admin';
import { PostsModerationDetail } from './PostsModerationDetail';
import { usePhotoPrivacyJob } from './usePhotoPrivacyJob';
import { ModerationMap } from './ModerationMap';
import { aggregateVerificationCounts, VerificationInlineSummary } from './verificationSummary';
import {
  bulkConfirmMessage,
  runBulkModeration,
  type BulkAction as BulkModerationAction,
} from './bulkModeration';
import {
  deletePost,
  moderatePostContent,
  moderateVisibility,
  MODERATION_AI_STATUS_LABEL,
  moderationAiKeyOf,
  usePostsModeration,
  type ModerationAiKey,
  type ModerationContentEdit,
  type ModerationPost,
} from './usePostsModeration';
import { mergeFields } from '../data-settings';
import { useAiAnalyzePost } from './useAiAnalyzePost';
import { resolveChipColor, useAiCategoryMastersBySettings } from './useAiCategoryMastersBySettings';
import { useRepairPostThumbnails } from './useRepairPostThumbnails';

/**
 * 投稿モデレーション一覧 (admin 第 11 弾 + Sprint BG-DataSettings 22 で AI 再解析 / status filter /
 * keyword 検索 / chip filter / AI 詳細 panel を旧 v1 から復元、Phase 5.4 REQ-4.3.9-13)。
 *
 * 旧 `old/apps/admin/src/pages/PostsModerationPage.tsx` (1179 行) から本 sprint で復元:
 *   - L150-212: AI 再解析 (single / bulk-failed) UX。callable 名は v2 で aiAnalyzePost に統一
 *   - L548-899: AI 解析結果 detail panel (ECW: emotions + categories chips + reasons)
 *   - keyword 検索 (title + comment 部分一致) + AI status filter + emotion/category chip filter
 *   - 一覧の AI status badge
 *
 * Admin Wave AW5 (docs/108) で追加復元:
 *   - 一覧 / 地図 タブ切替 + ModerationMap (visibility 別マーカー色 + 凡例 + クリックで詳細)
 *   - AI 検証フィルタ (未回答 / 回答あり / 妥当ではない多め ≥30%)
 *   - 一括再解析の 2 系統 (未解析・失敗の写真付き / 写真付き全件) + 従来の「失敗のみ」
 *   - 一覧カードに写真サムネ + category プレーン表示 + 検証集計サマリ
 *
 * 旧との差分:
 *   - 旧の reanalyzePost callable → v2 では aiAnalyzePost (force=true) に統一
 *   - chip color は M2 master 駆動 (旧 formatCategoryLabel と同等)
 */

const VISIBILITY_LABEL: Record<PostVisibility, string> = {
  public: '公開',
  hidden_others: '本人のみ',
  hidden_all: '全員非表示',
};

const VISIBILITY_BADGE: Record<PostVisibility, string> = {
  public: 'bg-success-600 text-white',
  hidden_others: 'bg-warning-600 text-white',
  hidden_all: 'bg-danger-700 text-white',
};

type StatusFilter = 'all' | PostVisibility;
type AiStatusFilter = 'all' | ModerationAiKey;
type VerifyFilter = 'all' | 'unanswered' | 'answered' | 'many_invalid';
type ViewTab = 'list' | 'map';

const VERIFY_FILTER_LABEL: Record<VerifyFilter, string> = {
  all: 'すべて',
  unanswered: '未回答',
  answered: '回答あり',
  many_invalid: '妥当ではない多め (≥30%)',
};

const AI_STATUS_LABEL: Record<AiStatusFilter, string> = {
  all: 'すべて',
  ...MODERATION_AI_STATUS_LABEL,
};

const AI_BADGE: Record<ModerationAiKey, string> = {
  done: 'bg-success-100 text-success-700 border border-success-300',
  failed: 'bg-danger-100 text-danger-700 border border-danger-300',
  pending: 'bg-warning-100 text-warning-700 border border-warning-300',
  processing: 'bg-warning-100 text-warning-700 border border-warning-300',
  skipped: 'bg-slate-200 text-slate-600 border border-slate-300',
  quota: 'bg-warning-100 text-warning-800 border border-warning-300',
  unanalyzed: 'bg-slate-100 text-slate-500 border border-slate-200',
};

function matchesAi(p: ModerationPost, f: AiStatusFilter): boolean {
  if (f === 'all') return true;
  return moderationAiKeyOf(p) === f;
}

/** 検証フィルタ (old2 PostsModerationPage L248-260 のロジック踏襲)。 */
function matchesVerify(p: ModerationPost, f: VerifyFilter): boolean {
  if (f === 'all') return true;
  const completed = p.verificationSummary?.completedResponses ?? 0;
  if (f === 'unanswered') return completed === 0;
  if (f === 'answered') return completed > 0;
  // many_invalid: 妥当ではない票が全体の 30% 以上
  if (completed === 0) return false;
  const c = aggregateVerificationCounts(p.verificationSummary);
  const total = c.validCount + c.neutralCount + c.invalidCount;
  if (total === 0) return false;
  return c.invalidCount / total >= 0.3;
}

/**
 * サムネイル不具合判定: 写真ありの投稿で「サムネ未生成 (photoThumbPath 無し) または
 * 一覧サムネの load 失敗 (保存済 photoThumbUrl のトークン失効等、runtime 検出)」。
 */
function isThumbBroken(p: ModerationPost, brokenIds: Set<string>): boolean {
  if (!p.hasPhoto) return false;
  return !p.photoThumbPath || brokenIds.has(p.id);
}

export function PostsModerationPage() {
  const { districtId } = useParams<{ districtId: string }>();
  const { state: authState } = useAdminAuth();
  const districtState = useDistrict(districtId ?? null);
  const moderationState = usePostsModeration(districtId ?? null);
  const { toast } = useToast();
  // 内容編集の入力項目フォーム駆動: 地区 mode テンプレ default + 保存値を merge した postFields。
  const postFields: FieldDef[] = useMemo(() => {
    const d = districtState.status === 'ready' ? districtState.district : null;
    const mode = (d?.mode as DistrictMode | undefined) ?? 'bousai';
    const tpl = getModeTemplate(mode);
    const saved = (d?.dataSettings ?? {}) as Partial<DataSettings>;
    return mergeFields(tpl.dataSettings.postFields, saved.postFields ?? [], 'postFields');
  }, [districtState]);
  // docs/29: 観点の表示名 (投稿者からの再解析の依頼に付いた観点を名前で出すため)。
  const analysisLabelOf = useMemo(() => {
    const d = districtState.status === 'ready' ? districtState.district : null;
    const options = listPostAnalysisOptions(d?.aiAnalysis ?? null);
    return (surveyKey: string | null): string => {
      if (!surveyKey) return options[0]?.label ?? '既定の観点';
      return options.find((o) => o.key === surveyKey)?.label ?? '追加の観点 (今は使われていません)';
    };
  }, [districtState]);
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [aiFilter, setAiFilter] = useState<AiStatusFilter>('all');
  // 投稿者からの AI 再解析の依頼がある投稿だけに絞り込む (docs/13 §9-2)。
  const [reanalysisRequestFilter, setReanalysisRequestFilter] = useState(false);
  // docs/28 §10-2: 写真のぼかしの状態で絞り込む。
  const [privacyFilter, setPrivacyFilter] = useState<PhotoPrivacyCategory | 'all'>('all');
  const [verifyFilter, setVerifyFilter] = useState<VerifyFilter>('all');
  const [viewTab, setViewTab] = useState<ViewTab>('list');
  const [keyword, setKeyword] = useState('');
  // 投稿者フィルタ: 'all' または authorUid (UI には出さず内部キーとしてのみ使用)。
  const [authorFilter, setAuthorFilter] = useState<string>('all');
  const [chipFilter, setChipFilter] = useState<{
    kind: 'emotion' | 'category';
    value: string;
  } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [analyzingId, setAnalyzingId] = useState<string | null>(null);
  const [bulkAnalyzeProgress, setBulkAnalyzeProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [selected, setSelected] = useState<ModerationPost | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const { analyze } = useAiAnalyzePost();
  // サムネイル不具合フィルタ: 「未生成 (photoThumbPath 無し) or 一覧サムネが load 失敗 (トークン失効等)」。
  const [thumbBrokenFilter, setThumbBrokenFilter] = useState(false);
  const [brokenThumbIds, setBrokenThumbIds] = useState<Set<string>>(new Set());
  const { busy: repairBusy, progress: repairProgress, repair } = useRepairPostThumbnails();
  const markThumbBroken = useCallback((postId: string) => {
    setBrokenThumbIds((prev) => (prev.has(postId) ? prev : new Set(prev).add(postId)));
  }, []);
  // 読めるようになった (URL が更新された等) サムネは「サムネ不具合」から外す。
  const markThumbLoaded = useCallback((postId: string) => {
    setBrokenThumbIds((prev) => {
      if (!prev.has(postId)) return prev;
      const next = new Set(prev);
      next.delete(postId);
      return next;
    });
  }, []);

  const isMaster = authState.role === 'master';
  const isDistrictAdminOfThis =
    authState.role === 'district' && !!districtId && authState.districts.includes(districtId);
  const canModerate = isMaster || isDistrictAdminOfThis;
  // docs/28 D13: ぼかし前の写真を見られるのは master と、地区設定で許可された地区の地区管理者。
  const canSeeOriginalPhoto =
    isMaster ||
    (isDistrictAdminOfThis &&
      districtState.status === 'ready' &&
      resolvePhotoPrivacySettings(districtState.district.photoPrivacy)
        .allowDistrictAdminViewOriginal);
  const uid = authState.user?.uid ?? '';

  const items = moderationState.status === 'ready' ? moderationState.items : [];

  // 詳細 modal は subscribe 済の最新 item を反映 (写真差し替えで append された photoReplacements 等)。
  const selectedLive = useMemo(
    () => (selected ? (items.find((p) => p.id === selected.id) ?? selected) : null),
    [selected, items],
  );

  // 最初に解析済 post の settingsId を取得 → chip master 解決に使用
  const settingsId = useMemo(() => {
    for (const p of items) if (p.aiAnalysisSettingsId) return p.aiAnalysisSettingsId;
    return null;
  }, [items]);
  const resolver = useAiCategoryMastersBySettings(settingsId);
  const emotionMaster = resolver.byTemplateVar.get('emotionMaster');
  const categoryMaster = resolver.byTemplateVar.get('categoryMaster');

  const photoJob = usePhotoPrivacyJob(districtId ?? null);
  const unprocessedCount = useMemo(
    () =>
      items.filter(
        (p) =>
          !p.archivedAt &&
          photoPrivacyCategory({ photoPath: p.photoPath, photoPrivacy: p.photoPrivacy }) ===
            'unprocessed',
      ).length,
    [items],
  );
  const failedPhotoCount = useMemo(
    () =>
      items.filter((p) => {
        if (p.archivedAt) return false;
        const c = photoPrivacyCategory({ photoPath: p.photoPath, photoPrivacy: p.photoPrivacy });
        return c === 'needs-action' || c === 'retrying';
      }).length,
    [items],
  );
  // docs/28 レビュー R2: 失敗した写真 (自動の再実行を使い切ったものを含む) をまとめてもう一度処理する。
  async function handleRetryFailedPhotos() {
    if (
      !confirm(
        `ぼかしの処理に失敗した写真 ${failedPhotoCount} 件を、まとめてもう一度処理します。実行しますか？`,
      )
    )
      return;
    try {
      const r = await photoJob.start('failed');
      toast('success', r.total > 0 ? `${r.total} 件の処理を始めました` : '対象の写真はありません');
    } catch (e) {
      toast('error', (e as Error).message || '開始できませんでした');
    }
  }
  async function handleStartPhotoPrivacyJob() {
    if (
      !confirm(
        `未処理の写真 ${unprocessedCount} 件の顔・ナンバーを検出してぼかし、写真を置き換えます。\n` +
          'ぼかし前の写真は地区の設定に従って保存します。すでにほかの人の端末に表示された写真は、その端末に最大 30 日残ることがあります。\n' +
          '実行しますか？',
      )
    )
      return;
    try {
      const r = await photoJob.start('unprocessed');
      toast('success', r.total > 0 ? `${r.total} 件の処理を始めました` : '対象の写真はありません');
    } catch (e) {
      toast('error', (e as Error).message || '開始できませんでした');
    }
  }

  const privacyCounts = useMemo(() => {
    const order: PhotoPrivacyCategory[] = [
      'needs-action',
      'retrying',
      'processing',
      'unprocessed',
      'detected',
      'not-detected',
      'withheld',
      'skipped',
    ];
    const counts = new Map<PhotoPrivacyCategory, number>();
    for (const p of items) {
      const c = photoPrivacyCategory({ photoPath: p.photoPath, photoPrivacy: p.photoPrivacy });
      counts.set(c, (counts.get(c) ?? 0) + 1);
    }
    return order.filter((c) => counts.has(c)).map((c) => [c, counts.get(c) ?? 0] as const);
  }, [items]);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return items.filter((p) => {
      if (filter !== 'all' && p.visibility !== filter) return false;
      if (authorFilter !== 'all' && p.authorUid !== authorFilter) return false;
      if (!matchesAi(p, aiFilter)) return false;
      if (
        privacyFilter !== 'all' &&
        photoPrivacyCategory({ photoPath: p.photoPath, photoPrivacy: p.photoPrivacy }) !==
          privacyFilter
      )
        return false;
      if (!matchesVerify(p, verifyFilter)) return false;
      if (reanalysisRequestFilter && !p.reanalysisRequest) return false;
      if (thumbBrokenFilter && !isThumbBroken(p, brokenThumbIds)) return false;
      if (kw) {
        const hay = `${p.title} ${p.comment ?? ''}`.toLowerCase();
        if (!hay.includes(kw)) return false;
      }
      if (chipFilter) {
        if (!p.aiAnalysisEcw) return false;
        if (chipFilter.kind === 'emotion') {
          return p.aiAnalysisEcw.emotions.includes(chipFilter.value);
        }
        return p.aiAnalysisEcw.categoryCodes.includes(chipFilter.value);
      }
      return true;
    });
  }, [
    items,
    filter,
    authorFilter,
    aiFilter,
    privacyFilter,
    verifyFilter,
    reanalysisRequestFilter,
    keyword,
    chipFilter,
    thumbBrokenFilter,
    brokenThumbIds,
  ]);

  // 検証フィルタ別件数 (ボタンラベルに件数を出す)。
  const verifyCounts = useMemo(() => {
    const c: Record<VerifyFilter, number> = {
      all: items.length,
      unanswered: 0,
      answered: 0,
      many_invalid: 0,
    };
    for (const p of items) {
      if (matchesVerify(p, 'unanswered')) c.unanswered++;
      if (matchesVerify(p, 'answered')) c.answered++;
      if (matchesVerify(p, 'many_invalid')) c.many_invalid++;
    }
    return c;
  }, [items]);

  const counts = useMemo(() => {
    const c: Record<PostVisibility, number> = { public: 0, hidden_others: 0, hidden_all: 0 };
    for (const p of items) c[p.visibility]++;
    return c;
  }, [items]);

  // 投稿者 (authorUid) 別の一覧 + 件数。表示は authorNickname、絞り込みキーは authorUid
  // (ニックネームは重複・改名しうるため uid をキーにする)。items は createdAt desc なので、
  // 各 uid で最初に出会う = 直近投稿のニックネームを代表名に採用する。
  // 注: 端末引き継ぎ (R-70) で同一人物が複数 uid を持つ場合は別投稿者として並ぶ。
  const authors = useMemo(() => {
    const m = new Map<string, { uid: string; nickname: string; count: number }>();
    for (const p of items) {
      const cur = m.get(p.authorUid);
      if (cur) cur.count++;
      else
        m.set(p.authorUid, {
          uid: p.authorUid,
          nickname: p.authorNickname || '(名前未設定)',
          count: 1,
        });
    }
    return Array.from(m.values()).sort((a, b) => a.nickname.localeCompare(b.nickname, 'ja'));
  }, [items]);

  const reanalysisRequestCount = useMemo(
    () => items.filter((p) => !!p.reanalysisRequest).length,
    [items],
  );
  const aiCounts = useMemo(() => {
    const c: Record<AiStatusFilter, number> = {
      all: items.length,
      done: 0,
      pending: 0,
      processing: 0,
      failed: 0,
      skipped: 0,
      quota: 0,
      unanalyzed: 0,
    };
    for (const p of items) c[moderationAiKeyOf(p)]++;
    return c;
  }, [items]);

  // AI 再解析の対象集合 (old2 PostsModerationPage L145-152 踏襲)。
  //   needsReanalysis = 写真付き かつ aiStatus が failed / pending / null (未実施・失敗)
  //   reanalyzableAll = 写真付き かつ processing 以外 (写真付き全件の再解析)
  const needsReanalysis = useMemo(
    () =>
      items.filter(
        (p) =>
          p.hasPhoto && (p.aiStatus === 'failed' || p.aiStatus === 'pending' || p.aiStatus == null),
      ),
    [items],
  );
  const reanalyzableAll = useMemo(
    () => items.filter((p) => p.hasPhoto && p.aiStatus !== 'processing'),
    [items],
  );

  const visibleIds = useMemo(() => new Set(filtered.map((p) => p.id)), [filtered]);
  const effectiveSelectedIds = useMemo(() => {
    const out = new Set<string>();
    for (const id of selectedIds) if (visibleIds.has(id)) out.add(id);
    return out;
  }, [selectedIds, visibleIds]);

  const allVisibleSelected = filtered.length > 0 && effectiveSelectedIds.size === filtered.length;

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (allVisibleSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((p) => p.id)));
    }
  }

  async function handleModerate(post: ModerationPost, next: PostVisibility) {
    if (!districtId || !canModerate) return;
    if (post.visibility === next) return;
    setBusyId(post.id);
    try {
      await moderateVisibility(districtId, post.id, next, uid);
      toast('success', `${post.title || '(無題)'} を ${VISIBILITY_LABEL[next]} に変更しました`);
      if (selected?.id === post.id) setSelected({ ...post, visibility: next });
    } catch (e) {
      toast('error', `モデレーション失敗: ${(e as Error).message}`);
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(post: ModerationPost) {
    if (!districtId || !canModerate) return;
    if (
      !confirm(`「${post.title || '(無題)'}」を完全に削除します。復元できません。よろしいですか？`)
    ) {
      return;
    }
    setBusyId(post.id);
    try {
      await deletePost(districtId, post.id);
      toast('success', '投稿を削除しました');
      if (selected?.id === post.id) setSelected(null);
    } catch (e) {
      toast('error', `削除失敗: ${(e as Error).message}`);
    } finally {
      setBusyId(null);
    }
  }

  /** 投稿内容 (タイトル / コメント / 入力項目 / 位置) を保存。toast は詳細 modal 側が表示する。 */
  async function handleSaveContent(post: ModerationPost, content: ModerationContentEdit) {
    if (!districtId || !canModerate) return;
    await moderatePostContent(districtId, post.id, content, uid);
  }

  /** 表示中 (サムネ不具合フィルタ) の投稿のサムネイルをまとめて再生成する。 */
  async function handleBulkRepair() {
    if (!districtId || !canModerate || repairBusy) return;
    const ids = filtered.filter((p) => p.hasPhoto).map((p) => p.id);
    if (ids.length === 0) return;
    if (!confirm(`表示中の ${ids.length} 件のサムネイルを再生成します。よろしいですか？`)) return;
    try {
      const r = await repair(districtId, ids);
      toast(
        'success',
        `サムネイル再生成: 成功 ${r.repaired} / 対象外 ${r.skipped} / 失敗 ${r.failed}`,
      );
      // 修復済は doc 更新 (新トークン URL) → onSnapshot で再描画。runtime 検出をリセット。
      setBrokenThumbIds(new Set());
    } catch (e) {
      toast('error', `一括再生成に失敗しました: ${(e as Error).message}`);
    }
  }

  /** surveyKey = 解析の観点 (docs/29)。未指定は既定の観点 (従来どおり)。 */
  async function handleAnalyzeSingle(post: ModerationPost, surveyKey: string | null = null) {
    if (!districtId || !canModerate) return;
    setAnalyzingId(post.id);
    try {
      const res = await analyze(districtId, post.id, true, surveyKey);
      if (res.ok) {
        toast('success', `${post.title || '(無題)'} を再解析しました`);
      } else {
        toast('error', `再解析失敗: ${res.error}`);
      }
    } catch (e) {
      toast('error', `再解析失敗: ${(e as Error).message ?? 'unknown'}`);
    } finally {
      setAnalyzingId(null);
    }
  }

  /**
   * 指定 targets を順次再解析する共通処理 (old2 reanalyzeAllFailed / reanalyzeNeedsAttention の統合)。
   * 進捗表示 + 500ms 間隔 (rate limit 緩和) は従来踏襲。
   */
  async function runBulkAnalyze(targets: ModerationPost[], confirmMessage: string) {
    if (!districtId || !canModerate) return;
    if (targets.length === 0) {
      toast('info', '再解析が必要な投稿はありません');
      return;
    }
    if (!confirm(confirmMessage)) return;
    setBulkAnalyzeProgress({ done: 0, total: targets.length });
    let ok = 0;
    let fail = 0;
    for (let i = 0; i < targets.length; i++) {
      const p = targets[i];
      if (!p) continue;
      try {
        const res = await analyze(districtId, p.id, true);
        if (res.ok) ok++;
        else fail++;
      } catch {
        fail++;
      }
      setBulkAnalyzeProgress({ done: i + 1, total: targets.length });
      // rate limit 緩和のため 500ms 間隔
      if (i < targets.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }
    setBulkAnalyzeProgress(null);
    toast(
      fail === 0 ? 'success' : 'info',
      `${ok} 件再解析を依頼しました${fail > 0 ? ` (${fail} 件失敗)` : ''}`,
    );
  }

  /** 失敗状態のみ (従来の挙動)。 */
  function handleAnalyzeAllFailed() {
    const targets = items.filter((p) => p.aiStatus === 'failed');
    if (targets.length === 0) {
      toast('info', '失敗状態の投稿はありません');
      return;
    }
    void runBulkAnalyze(targets, `失敗状態の ${targets.length} 件を再解析します。よろしいですか？`);
  }

  /** AI 未実施・失敗の写真付き投稿のみ抽出して一括解析 (old2 reanalyzeNeedsAttention)。 */
  function handleAnalyzeNeeds() {
    void runBulkAnalyze(
      needsReanalysis,
      `AI 未解析・失敗の写真付き投稿 ${needsReanalysis.length} 件を解析します。よろしいですか？`,
    );
  }

  /** 写真付き全件を一括再解析 (old2 reanalyzeAllFailed)。 */
  function handleAnalyzeAllWithPhoto() {
    void runBulkAnalyze(
      reanalyzableAll,
      `写真付き ${reanalyzableAll.length} 件を再解析します。よろしいですか？`,
    );
  }

  async function handleBulk(action: BulkModerationAction) {
    if (!districtId || !canModerate) return;
    const ids = Array.from(effectiveSelectedIds);
    if (ids.length === 0) return;
    if (!confirm(bulkConfirmMessage(ids.length, action))) return;
    setBulkBusy(true);
    try {
      const result = await runBulkModeration({
        districtId,
        postIds: ids,
        action,
        uid,
      });
      if (result.failed.length === 0) {
        toast('success', `${result.ok} 件処理しました`);
      } else {
        toast(
          result.ok > 0 ? 'success' : 'error',
          `${result.ok} 件成功 / ${result.failed.length} 件失敗`,
        );
      }
      setSelectedIds(new Set());
    } finally {
      setBulkBusy(false);
    }
  }

  function chipFilterLabel(): string | null {
    if (!chipFilter) return null;
    if (chipFilter.kind === 'emotion') return chipFilter.value;
    const row = resolver.byCategoryCd.get(chipFilter.value);
    return row?.category ?? chipFilter.value;
  }

  return (
    <>
      <header className="mb-6">
        <Link
          to={districtId ? `/districts/${districtId}` : '/districts'}
          className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
        >
          ← 地区詳細
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 mt-2">
          投稿モデレーション
          {districtState.status === 'ready' ? ` — ${districtState.district.name}` : ''}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          REQ-4.1.18 / REQ-4.3.9 — 投稿の可視性 (公開 / 本人のみ / 全員非表示) と削除 + AI 再解析。
        </p>
      </header>

      {!canModerate && (
        <Card padding="md" className="border-danger-600 mb-4">
          <p className="text-sm text-danger-700">
            この地区のモデレーション権限がありません (role: {authState.role ?? 'none'}、地区:{' '}
            {districtId})。
          </p>
        </Card>
      )}

      {moderationState.status === 'loading' && (
        <Card padding="md" aria-busy="true">
          <ul className="grid grid-cols-1 gap-2" data-testid="posts-loading-skeleton">
            {[0, 1, 2].map((i) => (
              <li
                key={i}
                className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded p-3"
              >
                <Skeleton variant="rect" width={64} height={64} />
                <div className="flex-1 space-y-2">
                  <Skeleton variant="text" width="40%" height={14} />
                  <Skeleton variant="text" width="80%" height={12} />
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {moderationState.status === 'error' && (
        <Card padding="md" className="border-danger-600">
          <p className="text-sm text-danger-700">読込エラー: {moderationState.error.message}</p>
        </Card>
      )}

      {moderationState.status === 'ready' && (
        <>
          {/* keyword 検索 */}
          <div className="mb-3">
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="タイトル・コメントで検索"
              aria-label="キーワード検索"
              className="w-full text-sm px-3 py-2 border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>

          {/* visibility filter */}
          <div className="mb-3 flex flex-wrap gap-2 items-center">
            <span className="text-sm text-slate-600 mr-1">表示:</span>
            {(['all', 'public', 'hidden_others', 'hidden_all'] as const).map((s) => {
              const active = filter === s;
              const label =
                s === 'all' ? `すべて (${items.length})` : `${VISIBILITY_LABEL[s]} (${counts[s]})`;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setFilter(s)}
                  className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${active ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* 投稿者フィルタ (ドロップダウン + 一覧カードの投稿者名クリックでも設定可) */}
          <div className="mb-3 flex flex-wrap gap-2 items-center">
            <label htmlFor="author-filter" className="text-sm text-slate-600 mr-1">
              投稿者:
            </label>
            <select
              id="author-filter"
              value={authorFilter}
              onChange={(e) => setAuthorFilter(e.target.value)}
              className="text-sm px-2 py-1.5 border border-slate-300 rounded bg-white max-w-[16rem] focus:outline-none focus:ring-1 focus:ring-primary-400"
            >
              <option value="all">すべての投稿者 ({items.length})</option>
              {authors.map((a) => (
                <option key={a.uid} value={a.uid}>
                  {a.nickname} ({a.count}) ・ID {shortUid(a.uid)}
                </option>
              ))}
            </select>
            {authorFilter !== 'all' && (
              <button
                type="button"
                onClick={() => setAuthorFilter('all')}
                className="text-xs text-primary-600 hover:underline"
              >
                解除
              </button>
            )}
          </div>

          {/* docs/28 §10-2: 写真のぼかしの状態 */}
          <div className="mb-3 flex flex-wrap gap-2 items-center">
            <label htmlFor="privacy-filter" className="text-sm text-slate-600 mr-1">
              写真のぼかし:
            </label>
            <select
              id="privacy-filter"
              value={privacyFilter}
              onChange={(e) => setPrivacyFilter(e.target.value as PhotoPrivacyCategory | 'all')}
              className="text-sm px-2 py-1.5 border border-slate-300 rounded bg-white max-w-[16rem] focus:outline-none focus:ring-1 focus:ring-primary-400"
              data-testid="privacy-filter"
            >
              <option value="all">すべて</option>
              {privacyCounts.map(([c, n]) => (
                <option key={c} value={c}>
                  {PHOTO_PRIVACY_CATEGORY_LABELS[c]} ({n})
                </option>
              ))}
            </select>
            {/* docs/28 §4-7・§10-3: 導入前の投稿 (未処理) にまとめてぼかしを掛ける */}
            {canModerate && unprocessedCount > 0 && photoJob.job?.status !== 'running' && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => void handleStartPhotoPrivacyJob()}
                disabled={photoJob.starting}
                data-testid="photo-privacy-job-start"
              >
                {photoJob.starting
                  ? '開始中…'
                  : `未処理の写真 ${unprocessedCount} 件にまとめてぼかしを掛ける`}
              </Button>
            )}
            {canModerate && failedPhotoCount > 0 && photoJob.job?.status !== 'running' && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => void handleRetryFailedPhotos()}
                disabled={photoJob.starting}
                data-testid="photo-privacy-job-retry-failed"
              >
                {`失敗した写真 ${failedPhotoCount} 件をまとめてもう一度処理`}
              </Button>
            )}
            {photoJob.job && (photoJob.job.status === 'running' || photoJob.job.cursor > 0) && (
              <span
                className="text-xs text-slate-600"
                role="status"
                data-testid="photo-privacy-job-progress"
              >
                {photoJob.job.status === 'running'
                  ? 'ぼかしを掛けています'
                  : '前回のまとめての処理'}
                : {photoJob.job.cursor} / {photoJob.job.total} 件（公開 {photoJob.job.published}
                ・失敗 {photoJob.job.failed}
                {photoJob.job.skipped > 0 ? `・対象外 ${photoJob.job.skipped}` : ''}）
              </span>
            )}
          </div>

          {/* AI status filter + bulk re-analysis */}
          <div className="mb-3 flex flex-wrap gap-2 items-center">
            <span className="text-sm text-slate-600 mr-1">AI:</span>
            {(
              [
                'all',
                'done',
                'pending',
                'processing',
                'failed',
                'skipped',
                'quota',
                'unanalyzed',
              ] as const
            ).map((s) => {
              const active = aiFilter === s;
              const label = `${AI_STATUS_LABEL[s]} (${aiCounts[s]})`;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setAiFilter(s)}
                  className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${active ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                >
                  {label}
                </button>
              );
            })}
            {canModerate && (
              <div className="ml-auto flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => handleAnalyzeAllFailed()}
                  disabled={aiCounts.failed === 0 || bulkAnalyzeProgress !== null}
                >
                  {bulkAnalyzeProgress
                    ? `再解析中 ${bulkAnalyzeProgress.done}/${bulkAnalyzeProgress.total}…`
                    : `🤖 失敗を再解析 (${aiCounts.failed})`}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => handleAnalyzeNeeds()}
                  disabled={needsReanalysis.length === 0 || bulkAnalyzeProgress !== null}
                  title="aiStatus が failed/pending/null の写真付き投稿のみ抽出して解析"
                >
                  {`未解析・失敗を一括解析 (${needsReanalysis.length})`}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => handleAnalyzeAllWithPhoto()}
                  disabled={reanalyzableAll.length === 0 || bulkAnalyzeProgress !== null}
                  title="写真付きの全投稿を再解析"
                >
                  {`写真付き全件を再解析 (${reanalyzableAll.length})`}
                </Button>
              </div>
            )}
          </div>

          {/* 投稿者からの AI 再解析の依頼 (docs/13 §9-2)。管理者が再解析すると依頼は消える。 */}
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setReanalysisRequestFilter((v) => !v)}
              aria-pressed={reanalysisRequestFilter}
              data-testid="reanalysis-request-filter"
              className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${reanalysisRequestFilter ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
            >
              {`🙋 再解析の依頼あり (${reanalysisRequestCount})`}
            </button>
          </div>

          {/* 検証フィルタ (old2 PostsModerationPage L370-388 踏襲) */}
          <div className="mb-3 flex flex-wrap gap-2 items-center">
            <span className="text-sm text-slate-600 mr-1">✓ 検証:</span>
            {(['all', 'unanswered', 'answered', 'many_invalid'] as const).map((s) => {
              const active = verifyFilter === s;
              const label =
                s === 'all'
                  ? `${VERIFY_FILTER_LABEL[s]} (${items.length})`
                  : `${VERIFY_FILTER_LABEL[s]} (${verifyCounts[s]})`;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setVerifyFilter(s)}
                  className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${active ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* サムネイル不具合フィルタ + 一括再生成 (未生成 / リンク失効で割れたサムネを抽出 → 修復) */}
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setThumbBrokenFilter((v) => !v)}
              className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${thumbBrokenFilter ? 'bg-warning-600 text-white border-warning-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
            >
              🖼 サムネイル不具合のみ
            </button>
            {thumbBrokenFilter && canModerate && (
              <Button
                size="sm"
                variant="primary"
                onClick={() => void handleBulkRepair()}
                disabled={repairBusy || filtered.length === 0}
              >
                {repairBusy
                  ? repairProgress
                    ? `再生成中… (${repairProgress.done}/${repairProgress.total})`
                    : '再生成中…'
                  : `表示中の ${filtered.length} 件を一括再生成`}
              </Button>
            )}
            {thumbBrokenFilter && (
              <span className="text-[11px] text-slate-500">
                未生成またはリンク切れのサムネを抽出。再生成しても写真は変わりません。
              </span>
            )}
          </div>

          {/* 一覧 / 地図 タブ切替 (old2 PostsModerationPage L390-404 踏襲) */}
          <div className="mb-3 flex gap-1">
            {(['list', 'map'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setViewTab(tab)}
                className={`px-4 py-1.5 text-sm rounded-t border border-b-0 ${viewTab === tab ? 'bg-white font-semibold border-slate-300' : 'bg-slate-100 text-slate-500 border-transparent'}`}
              >
                {tab === 'list' ? '一覧' : '地図'}
              </button>
            ))}
          </div>

          {/* chip filter active 表示 */}
          {chipFilter && (
            <div className="mb-3">
              <button
                type="button"
                onClick={() => setChipFilter(null)}
                aria-label="chip フィルタを解除"
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] text-white bg-primary-700 hover:bg-primary-800"
              >
                <span>
                  {chipFilter.kind === 'emotion' ? '感情' : 'カテゴリ'}: {chipFilterLabel()}
                </span>
                <span aria-hidden>×</span>
              </button>
            </div>
          )}

          {viewTab === 'map' ? (
            filtered.length === 0 ? (
              <EmptyState
                icon="🗺️"
                title="地図に表示できる投稿がありません"
                description="フィルタを変更すると、他の投稿が表示される可能性があります。"
              />
            ) : (
              <ModerationMap posts={filtered} onSelectPost={setSelected} />
            )
          ) : filtered.length === 0 ? (
            <EmptyState
              icon="📭"
              title="該当する投稿はありません"
              description={
                filter !== 'all' ||
                authorFilter !== 'all' ||
                aiFilter !== 'all' ||
                privacyFilter !== 'all' ||
                verifyFilter !== 'all' ||
                keyword ||
                chipFilter
                  ? 'フィルタを変更すると、他の投稿が表示される可能性があります。'
                  : 'この地区にはまだ投稿が登録されていません。'
              }
              action={
                filter !== 'all' ||
                authorFilter !== 'all' ||
                aiFilter !== 'all' ||
                privacyFilter !== 'all' ||
                verifyFilter !== 'all' ||
                keyword ||
                chipFilter
                  ? {
                      label: 'フィルタをリセット',
                      onClick: () => {
                        setFilter('all');
                        setAuthorFilter('all');
                        setAiFilter('all');
                        setPrivacyFilter('all');
                        setVerifyFilter('all');
                        setKeyword('');
                        setChipFilter(null);
                      },
                      variant: 'secondary',
                    }
                  : undefined
              }
            />
          ) : (
            <>
              {canModerate && (
                <div className="mb-2">
                  <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAll}
                      aria-label="全選択"
                      className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-400"
                    />
                    <span className="text-slate-700">
                      全選択 ({effectiveSelectedIds.size}/{filtered.length})
                    </span>
                  </label>
                </div>
              )}
              <ul className="grid grid-cols-1 gap-2">
                {filtered.map((p) => {
                  const isSelected = effectiveSelectedIds.has(p.id);
                  const aiKey = moderationAiKeyOf(p);
                  return (
                    <li key={p.id}>
                      <Card
                        padding="md"
                        className={`flex items-start gap-3 ${isSelected ? 'border-primary-500 ring-1 ring-primary-300' : ''}`}
                      >
                        {canModerate && (
                          <div className="shrink-0 pt-1">
                            <Checkbox
                              label=""
                              checked={isSelected}
                              onChange={() => toggleSelect(p.id)}
                            />
                          </div>
                        )}
                        {p.hasPhoto && (
                          <PostPhotoThumb
                            url={p.photoThumbUrl ?? p.photoUrl}
                            path={p.photoThumbPath ?? p.photoPath}
                            postId={p.id}
                            onLoadError={markThumbBroken}
                            onLoaded={markThumbLoaded}
                          />
                        )}
                        {/* docs/28: 写真のぼかしを待っている投稿 (まだ公開位置に写真が無い) */}
                        {!p.hasPhoto && p.photoPrivacy?.incomingPath && (
                          <div className="shrink-0 w-20 h-20 grid grid-cols-1 place-items-center bg-slate-100 rounded border border-slate-200 text-center text-[10px] text-slate-500 px-1">
                            写真を処理中
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span
                              className={`text-[10px] px-1.5 py-0.5 rounded ${VISIBILITY_BADGE[p.visibility]}`}
                            >
                              {VISIBILITY_LABEL[p.visibility]}
                            </span>
                            <span
                              className={`text-[10px] px-1.5 py-0.5 rounded ${AI_BADGE[aiKey]}`}
                              title={p.aiAnalysisError?.message ?? undefined}
                            >
                              AI: {AI_STATUS_LABEL[aiKey]}
                            </span>
                            <span className="text-xs text-slate-500">
                              <button
                                type="button"
                                onClick={() =>
                                  setAuthorFilter(
                                    authorFilter === p.authorUid ? 'all' : p.authorUid,
                                  )
                                }
                                className={`hover:underline hover:text-primary-700 ${authorFilter === p.authorUid ? 'font-semibold text-primary-700' : ''}`}
                                title={
                                  authorFilter === p.authorUid
                                    ? 'この投稿者の絞り込みを解除'
                                    : 'この投稿者だけ表示'
                                }
                              >
                                {p.authorNickname || '(名前未設定)'}
                              </button>
                              {' ・ '}
                              {p.createdAt ? p.createdAt.toLocaleString('ja-JP') : '日時不明'}
                            </span>
                          </div>
                          <p className="mt-1 text-sm font-bold text-slate-900 truncate">
                            {p.title || '(無題)'}
                          </p>
                          {p.comment && (
                            <p className="mt-1 text-xs text-slate-600 line-clamp-2 whitespace-pre-wrap">
                              {p.comment}
                            </p>
                          )}
                          {p.aiAnalysisEcw && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {p.aiAnalysisEcw.emotions.map((emotion) => {
                                const row = resolver.byCategoryLabel.get(emotion);
                                const color = row
                                  ? resolveChipColor(row, emotionMaster)
                                  : '#94A3B8';
                                const isActive =
                                  chipFilter?.kind === 'emotion' && chipFilter.value === emotion;
                                return (
                                  <button
                                    type="button"
                                    key={`em-${emotion}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setChipFilter(
                                        isActive ? null : { kind: 'emotion', value: emotion },
                                      );
                                    }}
                                    aria-label={`感情で絞込: ${emotion}`}
                                    className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium text-white cursor-pointer hover:opacity-80 ${
                                      isActive ? 'ring-2 ring-primary-700 ring-offset-1' : ''
                                    }`}
                                    style={{ backgroundColor: color }}
                                  >
                                    {emotion}
                                  </button>
                                );
                              })}
                              {p.aiAnalysisEcw.categoryCodes.map((code) => {
                                const row = resolver.byCategoryCd.get(code);
                                const color = row
                                  ? resolveChipColor(row, categoryMaster)
                                  : '#94A3B8';
                                const isActive =
                                  chipFilter?.kind === 'category' && chipFilter.value === code;
                                return (
                                  <button
                                    type="button"
                                    key={`cat-${code}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setChipFilter(
                                        isActive ? null : { kind: 'category', value: code },
                                      );
                                    }}
                                    aria-label={`カテゴリで絞込: ${row?.category ?? code}`}
                                    className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium text-white cursor-pointer hover:opacity-80 ${
                                      isActive ? 'ring-2 ring-primary-700 ring-offset-1' : ''
                                    }`}
                                    style={{ backgroundColor: color }}
                                  >
                                    {row?.category ?? code}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                          {p.reanalysisRequest && (
                            <p className="mt-1">
                              <Badge variant="warning" size="sm">
                                再解析の依頼あり
                              </Badge>
                            </p>
                          )}
                          <VerificationInlineSummary summary={p.verificationSummary} />
                        </div>
                        <div className="shrink-0 flex flex-col gap-1">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setSelected(p)}
                            disabled={busyId === p.id}
                          >
                            詳細
                          </Button>
                          {canModerate && (
                            <select
                              value={p.visibility}
                              onChange={(e) =>
                                void handleModerate(p, e.target.value as PostVisibility)
                              }
                              disabled={busyId === p.id}
                              aria-label="visibility 変更"
                              className="text-xs border border-slate-300 rounded px-1 py-0.5"
                            >
                              <option value="public">公開</option>
                              <option value="hidden_others">本人のみ</option>
                              <option value="hidden_all">全員非表示</option>
                            </select>
                          )}
                          {canModerate && (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => void handleAnalyzeSingle(p)}
                              disabled={analyzingId === p.id || bulkAnalyzeProgress !== null}
                              aria-label={`${p.title || '(無題)'} を再解析`}
                            >
                              {analyzingId === p.id ? '解析中…' : '🤖 再解析'}
                            </Button>
                          )}
                        </div>
                      </Card>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </>
      )}

      {canModerate && (
        <BulkActionsBar
          selectedCount={effectiveSelectedIds.size}
          busy={bulkBusy}
          itemLabel="件の投稿"
          onClear={() => setSelectedIds(new Set())}
          actions={[
            {
              id: 'public',
              label: '公開に変更',
              onClick: () => void handleBulk({ kind: 'visibility', visibility: 'public' }),
            },
            {
              id: 'hidden_others',
              label: '本人のみ',
              onClick: () => void handleBulk({ kind: 'visibility', visibility: 'hidden_others' }),
            },
            {
              id: 'hidden_all',
              label: '全員非表示',
              onClick: () => void handleBulk({ kind: 'visibility', visibility: 'hidden_all' }),
            },
            {
              id: 'delete',
              label: '削除',
              variant: 'danger',
              onClick: () => void handleBulk({ kind: 'delete' }),
            },
          ]}
        />
      )}

      {selected && selectedLive && districtId && (
        <PostsModerationDetail
          districtId={districtId}
          post={selectedLive}
          canModerate={canModerate}
          canSeeOriginal={canSeeOriginalPhoto}
          busy={busyId === selected.id}
          analyzing={analyzingId === selected.id}
          postFields={postFields}
          onClose={() => setSelected(null)}
          onModerate={(v) => handleModerate(selected, v)}
          onDelete={() => handleDelete(selected)}
          onSaveContent={(content) => handleSaveContent(selected, content)}
          onAnalyze={() => handleAnalyzeSingle(selected)}
          reanalysisRequestLabel={
            selectedLive.reanalysisRequest
              ? analysisLabelOf(selectedLive.reanalysisRequest.surveyKey)
              : undefined
          }
          onAnalyzeRequested={
            selectedLive.reanalysisRequest?.surveyKey
              ? () =>
                  handleAnalyzeSingle(selected, selectedLive.reanalysisRequest?.surveyKey ?? null)
              : undefined
          }
        />
      )}
    </>
  );
}

/**
 * 一覧カードの写真サムネ。download URL (photoThumbUrl/photoUrl) があればそれを直接使い、
 * 無い旧投稿のみ Storage path から getDownloadURL で解決する (docs/106、ネットワーク節約)。
 */
function PostPhotoThumb({
  url,
  path,
  postId,
  onLoadError,
  onLoaded,
}: {
  url: string | null;
  path: string | null;
  /** サムネ不具合検出用 (img load 失敗を親へ通知)。 */
  postId?: string;
  onLoadError?: (postId: string) => void;
  /** 読めたとき (サムネ不具合の表示を外す)。 */
  onLoaded?: (postId: string) => void;
}) {
  const [resolved, setResolved] = useState<string | null>(url);
  const [failed, setFailed] = useState(false);

  function reportError() {
    setFailed(true);
    if (postId && onLoadError) onLoadError(postId);
  }

  useEffect(() => {
    // docs/28: URL が変わった (ぼかしの作り直し等) ら読込失敗の表示を戻して読み直す。
    setFailed(false);
    if (url) {
      setResolved(url);
      return;
    }
    if (!path) {
      setResolved(null);
      return;
    }
    let cancelled = false;
    setResolved(null);
    setFailed(false);
    getDownloadURL(storageRef(storage, path))
      .then((u) => {
        if (!cancelled) setResolved(u);
      })
      .catch(() => {
        if (!cancelled) reportError();
      });
    return () => {
      cancelled = true;
    };
    // reportError は postId/onLoadError 由来で安定扱い (codebase 既定の suppress に倣う)。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, path]);

  return (
    <div className="shrink-0 w-20 h-20 grid grid-cols-1 place-items-center bg-slate-100 rounded border border-slate-200 overflow-hidden">
      {resolved ? (
        // 保存済 photoThumbUrl を直接描画するため、トークン失効 (403) は img onError で検出できる。
        <img
          src={resolved}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
          onError={reportError}
          onLoad={() => postId && onLoaded?.(postId)}
        />
      ) : failed ? (
        <span className="text-[10px] text-slate-400 px-1 text-center">読込失敗</span>
      ) : (
        <span className="text-[10px] text-slate-400">…</span>
      )}
    </div>
  );
}
